import React, { useMemo, useState, useContext } from 'react';
import { AuthContext } from './AuthContext';
import StudentProfile from './StudentProfile';
import { resolveMediaUrl } from '../utilities/media';

const studentTabs = [
  { id: 'overview', label: 'Resumen' },
  { id: 'learning', label: 'Mi aprendizaje' },
  { id: 'profile', label: 'Perfil' },
  { id: 'activity', label: 'Actividad' }
];

const StudentDashboard = ({ onNavigate, onLogout, onSelectCourse }) => {
  const { currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');

  const enrolledCourses = useMemo(() => {
    const apiCourses = Array.isArray(currentUser?.studentCourses) ? currentUser.studentCourses : [];

    return apiCourses
      .filter((course) => course?.id)
      .map((course, index) => ({
        ...course,
        thumbnail: resolveMediaUrl(
          course.thumbnail_url,
          'https://placehold.co/640x360/e5e7eb/64748b?text=Curso'
        ),
        instructor: course.instructor?.name || course.instructor?.email || 'Instructor',
        rating: Number(course.rating || 0) || 4.7,
        sections: Array.isArray(course.sections) ? course.sections : [],
        progress: Number(course.progress || 0) || Math.min(92, 35 + index * 18)
      }));
  }, [currentUser]);

  const dashboardStats = useMemo(() => {
    const courseCount = enrolledCourses.length;
    const averageProgress = courseCount
      ? Math.round(enrolledCourses.reduce((acc, course) => acc + (course.progress || 0), 0) / courseCount)
      : 0;
    const inProgress = enrolledCourses.filter((course) => (course.progress || 0) < 100).length;

    return {
      courseCount,
      averageProgress,
      inProgress
    };
  }, [enrolledCourses]);

  const continueLearning = (courseId) => {
    if (onSelectCourse) {
      onSelectCourse(courseId);
      return;
    }
    onNavigate?.('learnCourse');
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <section className="rounded-3xl bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-900 text-white p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-cyan-200 text-sm uppercase tracking-[0.22em] mb-3">Panel del estudiante</p>
            <h1 className="text-3xl md:text-4xl font-semibold max-w-2xl">
              {currentUser?.first_name || 'Estudiante'}, sigue construyendo tu ruta de aprendizaje.
            </h1>
            <p className="mt-3 text-slate-300 max-w-2xl">
              Unifiqué tus módulos de aprendizaje, perfil y actividad en una experiencia más cercana a EduDigital, adaptada a Educabolivia.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 min-w-full xl:min-w-[420px]">
            <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
              <p className="text-sm text-slate-300">Cursos activos</p>
              <p className="text-3xl font-semibold mt-2">{dashboardStats.courseCount}</p>
            </div>
            <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
              <p className="text-sm text-slate-300">En progreso</p>
              <p className="text-3xl font-semibold mt-2">{dashboardStats.inProgress}</p>
            </div>
            <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
              <p className="text-sm text-slate-300">Avance promedio</p>
              <p className="text-3xl font-semibold mt-2">{dashboardStats.averageProgress}%</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.3fr,0.7fr] gap-6">
        <div className="rounded-3xl bg-white shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Continúa aprendiendo</h2>
              <p className="text-sm text-slate-500">Tus cursos más relevantes en un solo lugar.</p>
            </div>
            <button
              onClick={() => setActiveTab('learning')}
              className="text-sm font-medium text-slate-900 hover:text-cyan-700"
            >
              Ver todos
            </button>
          </div>

          {enrolledCourses.length > 0 ? (
            <div className="space-y-4">
              {enrolledCourses.slice(0, 3).map((course) => (
                <div key={course.id} className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-4 rounded-2xl border border-slate-100 p-4">
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-36 object-cover rounded-2xl"
                  />
                  <div className="flex flex-col">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">{course.instructor}</p>
                    <h3 className="text-xl font-semibold text-slate-900">{course.title}</h3>
                    <p className="text-sm text-slate-500 mt-2">
                      {course.sections?.length || 0} secciones disponibles · Calificación {course.rating}
                    </p>
                    <div className="mt-4">
                      <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-cyan-500"
                          style={{ width: `${course.progress || 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-2">{course.progress || 0}% completado</p>
                    </div>
                    <div className="mt-auto pt-4">
                      <button
                        onClick={() => continueLearning(course.id)}
                        className="px-4 py-2 rounded-xl bg-slate-950 text-white hover:bg-slate-800"
                      >
                        Continuar aprendizaje
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center">
              <p className="text-slate-600 mb-4">Todavía no tienes cursos inscritos en tu perfil.</p>
              <button
                onClick={() => onNavigate?.('home')}
                className="px-4 py-2 bg-slate-950 text-white rounded-xl hover:bg-slate-800"
              >
                Explorar cursos
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl bg-white shadow-sm border border-slate-100 p-6">
            <h2 className="text-xl font-semibold mb-4">Atajos</h2>
            <div className="space-y-3">
              <button
                onClick={() => setActiveTab('profile')}
                className="w-full text-left rounded-2xl border border-slate-200 px-4 py-4 hover:border-slate-950 transition-colors"
              >
                Editar perfil y privacidad
              </button>
              <button
                onClick={() => onNavigate?.('messages')}
                className="w-full text-left rounded-2xl border border-slate-200 px-4 py-4 hover:border-slate-950 transition-colors"
              >
                Revisar mensajes
              </button>
              <button
                onClick={() => onNavigate?.('profile')}
                className="w-full text-left rounded-2xl border border-slate-200 px-4 py-4 hover:border-slate-950 transition-colors"
              >
                Abrir configuración completa
              </button>
            </div>
          </div>

          <div className="rounded-3xl bg-white shadow-sm border border-slate-100 p-6">
            <h2 className="text-xl font-semibold mb-4">Perfil visible</h2>
            <div className="space-y-3 text-sm text-slate-600">
              <p><span className="font-medium text-slate-900">Nombre:</span> {currentUser?.name || 'Usuario'}</p>
              <p><span className="font-medium text-slate-900">Título:</span> {currentUser?.title || 'Sin definir'}</p>
              <p><span className="font-medium text-slate-900">Idioma:</span> {currentUser?.language || 'Español'}</p>
              <p><span className="font-medium text-slate-900">Perfil público:</span> {currentUser?.settings?.showProfile ? 'Activo' : 'Oculto'}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderLearning = () => (
    <div className="rounded-3xl bg-white shadow-sm border border-slate-100 p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Mi aprendizaje</h2>
          <p className="text-sm text-slate-500">Una vista integrada de cursos, avance y acceso rápido.</p>
        </div>
        <button
          onClick={() => onNavigate?.('home')}
          className="px-4 py-2 rounded-xl border border-slate-300 hover:border-slate-900"
        >
          Explorar más cursos
        </button>
      </div>

      {enrolledCourses.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {enrolledCourses.map((course) => (
            <div key={course.id} className="rounded-2xl border border-slate-100 overflow-hidden">
              <img
                src={course.thumbnail}
                alt={course.title}
                className="w-full h-44 object-cover"
              />
              <div className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">{course.instructor}</p>
                <h3 className="text-xl font-semibold text-slate-900">{course.title}</h3>
                <p className="text-sm text-slate-500 mt-2">
                  {course.sections?.length || 0} secciones · Progreso estimado {course.progress || 0}%
                </p>
                <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mt-4">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${course.progress || 0}%` }} />
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    onClick={() => continueLearning(course.id)}
                    className="px-4 py-2 rounded-xl bg-slate-950 text-white hover:bg-slate-800"
                  >
                    Continuar
                  </button>
                  <button
                    onClick={() => continueLearning(course.id)}
                    className="px-4 py-2 rounded-xl border border-slate-300 hover:border-slate-900"
                  >
                    Abrir curso
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-600">
          Aún no tienes cursos en tu biblioteca.
        </div>
      )}
    </div>
  );

  const renderActivity = () => (
    <div className="grid grid-cols-1 xl:grid-cols-[0.9fr,1.1fr] gap-6">
      <div className="rounded-3xl bg-white shadow-sm border border-slate-100 p-6">
        <h2 className="text-2xl font-semibold text-slate-900 mb-4">Actividad y comunicación</h2>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-900">Mensajes</p>
            <p className="text-sm text-slate-500 mt-1">Tienes el módulo de mensajes disponible para continuar conversaciones con instructores.</p>
            <button
              onClick={() => onNavigate?.('messages')}
              className="mt-4 text-sm font-medium text-slate-900 hover:text-cyan-700"
            >
              Abrir mensajes
            </button>
          </div>
          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-900">Privacidad del perfil</p>
            <p className="text-sm text-slate-500 mt-1">
              Estado actual: {currentUser?.settings?.showProfile ? 'visible para usuarios autenticados' : 'oculto'}.
            </p>
            <button
              onClick={() => setActiveTab('profile')}
              className="mt-4 text-sm font-medium text-slate-900 hover:text-cyan-700"
            >
              Ajustar configuración
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white shadow-sm border border-slate-100 p-6">
        <h2 className="text-2xl font-semibold text-slate-900 mb-4">Próximos pasos sugeridos</h2>
        <div className="space-y-4">
          {[
            'Completa tu biografía para personalizar la experiencia.',
            'Revisa tus cursos en progreso y retoma el último módulo.',
            'Mantén visibles los cursos en tu perfil si quieres una experiencia más social.'
          ].map((item) => (
            <div key={item} className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-4 text-slate-700">
              {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'learning':
        return renderLearning();
      case 'profile':
        return <StudentProfile embedded />;
      case 'activity':
        return renderActivity();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-[280px,1fr] gap-6">
          <aside className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 h-fit xl:sticky xl:top-24">
            <div className="flex items-center gap-4 pb-6 border-b border-slate-100">
              <div className="w-14 h-14 rounded-full bg-slate-950 text-white flex items-center justify-center text-lg font-semibold">
                {currentUser?.first_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{currentUser?.name || 'Usuario'}</p>
                <p className="text-sm text-slate-500">{currentUser?.email || ''}</p>
              </div>
            </div>

            <nav className="mt-6 space-y-2">
              {studentTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left rounded-2xl px-4 py-3 transition-colors ${
                    activeTab === tab.id
                      ? 'bg-slate-950 text-white'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>

            <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
              <button
                onClick={() => onNavigate?.('profile')}
                className="w-full text-left rounded-2xl px-4 py-3 text-slate-600 hover:bg-slate-50"
              >
                Configuración completa
              </button>
              <button
                onClick={onLogout}
                className="w-full text-left rounded-2xl px-4 py-3 text-red-600 hover:bg-red-50"
              >
                Cerrar sesión
              </button>
            </div>
          </aside>

          <main>{renderContent()}</main>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
